import { Cabin, PremiumCabinCategoryID } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { sortBy } from 'lodash-es'
import { Cabin as BewotecCabin, GetCabinRequest } from '../../../bewotec/openapi'
import { handleError, handleWarning } from '../../../general/errorHandling'
import { globalHelper } from '../../../general/helper'
import { COMFORTAI, IND, INDAI, PremiumCabinCategoryIDs } from '../../../general/master-data'
import { useCabinStore, useCruiseStore, useDetailsStore, useUserStore } from '../../../store'
import { appHelper, mapAgencyForRequest } from '../../../store/app/helper'
import { cabinHelper } from './helper.js'
import { CabinCategory, IDetailDeck } from './interfaces'

export const fetchMoreCabins = async (categoryId: PremiumCabinCategoryID) => {
  const userStore = useUserStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()
  const cabinStore = useCabinStore()

  const priceModel = detailsStore.priceModel
  const cruiseId = cruiseStore.cruise!.id

  cabinStore.isCabinLoading = true

  const cabins = await cabinHelper.getCabins(
    detailsStore.paxConfig!,
    cruiseId,
    categoryId,
    userStore.agencyId,
    priceModel!
  )

  cabinStore.isCabinLoading = false

  const storedCabins = cabinStore.cabins

  if (!cabins.length && !storedCabins.length) {
    handleWarning('no matching cabin found', {
      categoryId,
      priceModel,
      cruiseId,
    })
  }

  cabinStore.cabins = !!storedCabins.length ? cabinHelper.filterDuplicates(storedCabins, cabins) : cabins
  deckAvailability()

  if (!!cabins.length) {
    cabinStore.setDeckAndCabin(cabins[0])
  }
}

export const fetchCabins = async () => {
  const userStore = useUserStore()
  const cabinStore = useCabinStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  cabinStore.cabins = []
  cabinStore.cabin = null
  cabinStore.selectedByUser = false
  cabinStore.userChoice = ''
  cabinStore.userChoiceMessage = 'Bitte Kabinennummer eingeben.'
  cabinStore.isCabinLoading = false

  let categories: CabinCategory[] = cabinStore.categories
  const PremiumCabinCategoryIds: PremiumCabinCategoryID[] = PremiumCabinCategoryIDs

  if (!categories.length) return

  categories = sortBy(
    categories.filter(cat => cat.available && PremiumCabinCategoryIds.includes(cat.id as PremiumCabinCategoryID)),
    'price'
  )

  const category = categories[0]

  if (!category) return

  cabinStore.isCabinLoading = true

  const cabins = await cabinHelper.getCabins(
    detailsStore.paxConfig!,
    cruiseStore.cruise!.id,
    category.id as PremiumCabinCategoryID,
    userStore.agencyId,
    detailsStore.priceModel!
  )

  cabinStore.isCabinLoading = false
  cabinStore.cabins = cabins

  deckAvailability()
}

export const setDeckAndCabin = (matchingCabin: Cabin) => {
  const cabinStore = useCabinStore()

  cabinStore.cabin = matchingCabin

  for (const deck of cabinStore.decks) {
    if (deck.id === matchingCabin.deck) {
      cabinStore.deck = deck
      break
    }
  }
}

export const preselectCategory = () => {
  const cabinStore = useCabinStore()
  const detailsStore = useDetailsStore()

  const priceModel = detailsStore.priceModel!
  const categories: CabinCategory[] = cabinStore.categories

  let selector = (_category: CabinCategory) => true

  if ([COMFORTAI, IND, INDAI].includes(priceModel)) {
    selector = category => category.type === cabinStore.type && category.available
  } else {
    selector = category => category.available
  }

  for (const category of categories) {
    if (selector(category)) {
      cabinStore.category = category
      break
    }
  }
}

export const preselectCabin = async () => {
  const cabinStore = useCabinStore()

  const category = cabinStore.category
  const matchingCabin = cabinStore.matchingCabins?.[0]

  if (!category || ![IND, INDAI, COMFORTAI].includes(category.priceModel)) return

  cabinStore.selectedByUser = false

  if (!matchingCabin) {
    await cabinStore.fetchMoreCabins(category.id as PremiumCabinCategoryID)
  }

  if (!!matchingCabin) {
    cabinStore.setDeckAndCabin(matchingCabin)
  }
}

export const deckAvailability = () => {
  const cabinStore = useCabinStore()
  const decks: IDetailDeck[] = cabinStore.decks

  for (const deck of decks) {
    deck.available = false

    for (const cabin of cabinStore.matchingCabins) {
      if (cabin.deck === deck.id) {
        deck.available = true
        break
      }
    }
  }

  cabinStore.decks = decks
}

export const selectType = (type: string) => {
  const cabinStore = useCabinStore()

  cabinStore.type = type

  preselectCategory()
  preselectCabin()
  deckAvailability()
}

export const selectCategory = (categoryId: string) => {
  const cabinStore = useCabinStore()
  const categories: CabinCategory[] = cabinStore.categories

  for (const category of categories) {
    if (category.id === categoryId) {
      cabinStore.category = category
      break
    }
  }

  preselectCabin()
  deckAvailability()
}

export const selectCabin = (cabinId: string) => {
  const cabinStore = useCabinStore()

  for (const cabin of cabinStore.cabins) {
    if (cabin.id === cabinId) {
      cabinStore.cabin = cabin
      cabinStore.selectedByUser = true
      break
    }
  }
}

export const selectDeck = (deckId: string) => {
  const cabinStore = useCabinStore()

  const id = parseInt(deckId, 10)

  for (const deck of cabinStore.decks) {
    if (deck.id === id) {
      cabinStore.deck = deck
      break
    }
  }
  const deck = cabinStore.deck
  const matchingCabins = cabinStore.matchingCabins
  const cabin = !!deck ? matchingCabins.find(cab => cab.deck === deck.id) : null

  if (!!cabin) {
    cabinStore.cabin = cabin
    cabinStore.selectedByUser = false
  }
}

export const checkUserChoice = async () => {
  const userStore = useUserStore()
  const cabinStore = useCabinStore()
  const cruiseStore = useCruiseStore()
  const detailsStore = useDetailsStore()

  cabinStore.userChoiceMessage = 'Prüfe Verfügbarkeit ...'

  const priceModel = detailsStore.priceModel!
  const paxConfig = detailsStore.paxConfig!
  const requestId = globalHelper.generateRequestId('CAB')
  let cabinResponse: BewotecCabin | null = null
  const cabinId = parseInt(cabinStore.userChoice, 10)

  try {
    const cabinRequest: GetCabinRequest = {
      cruiseId: cruiseStore.cruise!.id,
      cabinNumber: cabinId,
      agencyId: mapAgencyForRequest(userStore.agencyId),
      priceModelCode: priceModel,
      adults: paxConfig.adults,
      juveniles: paxConfig.juveniles,
      children: paxConfig.children,
      babies: paxConfig.babies,
      xTrackingIdentifier: requestId,
    }

    const cabinRawResponse = await appHelper.bewotecMicroServiceApi().getCabinRaw(cabinRequest)

    if (cabinRawResponse.raw.headers.get('x-tracking-identifier') !== requestId) return

    cabinResponse = await cabinRawResponse.value()
  } catch (error: any) {
    const errorCode = (await error.response.json()).code
    const ship = cruiseStore.ship

    switch (errorCode) {
      case 'BEWO_9999':
        cabinStore.userChoiceMessage = `${ship?.name} hat keine Kabine mit der Nr. ${cabinId}.`
        return

      case 'BEWO_1000':
      case 'BEWO_5071':
        cabinStore.userChoiceMessage = 'Kabine nicht verfügbar.'
        return

      default:
        cabinStore.userChoiceMessage = `${ship?.name} hat keine Kabine mit der Nr. ${cabinId}.`
        handleError({ error, origin: `checkUserChoice - errorCode ${errorCode} not implemented` })
        return
    }
  }

  cabinStore.cabin = !!cabinResponse ? cabinHelper.mapCabin(cabinResponse) : null

  if (!cabinStore.cabin) {
    cabinStore.userChoiceMessage = 'Kabine nicht verfügbar.'
    return
  }
  
  cabinStore.selectedByUser = true
  cabinStore.userChoiceMessage = 'Kabine verfügbar.'

  {
    const categories: CabinCategory[] = cabinStore.categories ?? []

    for (const category of categories) {
      if (category.id === cabinStore.cabin.cabinCategory) {
        cabinStore.category = category
        break
      }
    }

    const category: CabinCategory | null = cabinStore.category

    if (!!category) {
      cabinStore.type = category.type ?? ''

      if (!cabinStore.matchingCabins[0]) {
        await cabinStore.fetchMoreCabins(category.id as PremiumCabinCategoryID)
      }
    }
  }

  {
    deckAvailability()

    const decks: IDetailDeck[] = cabinStore.decks ?? []

    for (const deck of decks) {
      if (deck.id === cabinStore.cabin.deck) {
        cabinStore.deck = deck
        break
      }
    }
  }
}

export default {
  fetchCabins,
  preselectCategory,
  preselectCabin,
  selectType,
  selectCategory,
  selectCabin,
  selectDeck,
  checkUserChoice,
  setDeckAndCabin,
  fetchMoreCabins,
  deckAvailability,
}
